import { ChevronDown, Download, ExternalLink, Search } from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  workflowWebSearch,
  type WorkflowWebSearchResult,
} from 'sprites-types/workflow/tools/workflowWebSearch'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function WorkflowWebSearchDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(workflowWebSearch, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const parsed = parseResult.data

  // Handle emitMode for search results (schema emits object with results array)
  const resultObjects: WorkflowWebSearchResult[] = Array.isArray((parsed as any)?.results)
    ? [parsed as WorkflowWebSearchResult]
    : Array.isArray(parsed)
      ? (parsed as WorkflowWebSearchResult[])
      : [parsed as WorkflowWebSearchResult]

  // Flatten into a single list of results
  const items = resultObjects.flatMap((r) => r.results)

  // Header actions
  const headerActions: ContextAction[] = [
    {
      id: 'export-results',
      label: 'Export All',
      icon: <Download className="h-4 w-4" />,
      onClick: () => {
        console.log('Export web search results:', items)
      },
    },
  ]

  return (
    <div className="space-y-3">
      {/* Header with context menu and collapse/expand */}
      <ContextMenuWrapper actions={headerActions}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Search className="h-5 w-5 text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">Web Search Results</div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {items.length} items
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {/* Individual search results - show/hide based on expanded state */}
      {isExpanded && (
        <div className="space-y-2">
          {items.map((item) => {
            const contextActions: ContextAction[] = [
              {
                id: 'open-result',
                label: 'Open Link',
                icon: <ExternalLink className="h-4 w-4" />,
                onClick: () => window.open(item.url, '_blank'),
              },
            ]

            return (
              <ContextMenuWrapper key={item.url} actions={contextActions}>
                <div className="mx-8 rounded-3xl border border-gray-200/50 bg-white p-4 text-black shadow-sm backdrop-blur-[8px]">
                  <div className="min-w-0">
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="block truncate text-sm font-semibold text-blue-600 hover:underline"
                    >
                      {item.title}
                    </a>
                    <div className="truncate text-[11px] text-gray-500">{item.url}</div>
                    {item.snippet && (
                      <div className="mt-1 line-clamp-3 text-xs text-gray-700">{item.snippet}</div>
                    )}
                    {item.highlights && item.highlights.length > 0 && (
                      <ul className="mt-2 grid grid-cols-2 gap-2">
                        {item.highlights.map((h, idx) => (
                          <li
                            key={`${item.url}-h-${idx}`}
                            className="rounded-md bg-gray-100 px-2 py-1 text-[11px] text-gray-600"
                          >
                            {h}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </ContextMenuWrapper>
            )
          })}
        </div>
      )}
    </div>
  )
}

function downloadWorkflowWebSearch(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function workflowWebSearchToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(workflowWebSearch, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const parsed = parseResult.data

  const resultObjects: WorkflowWebSearchResult[] = Array.isArray((parsed as any)?.results)
    ? [parsed as WorkflowWebSearchResult]
    : Array.isArray(parsed)
      ? (parsed as WorkflowWebSearchResult[])
      : [parsed as WorkflowWebSearchResult]

  const items = resultObjects.flatMap((r) => r.results)

  if (items.length === 0) {
    return `## Web Search Results

No results found.

---
`
  }

  const resultsMarkdown = items
    .map((item) => {
      return `### ${item.title}

**URL:** ${item.url}
${item.snippet ? `**Snippet:** ${item.snippet}` : ''}

${
  item.highlights && item.highlights.length > 0
    ? `**Highlights:**
${item.highlights.map((h) => `- ${h}`).join('\n')}`
    : ''
}
`
    })
    .join('\n---\n\n')

  return `## Web Search Results (${items.length} results)

${resultsMarkdown}

---
`
}

export const WorkflowWebSearchDisplay = createWorkflowToolComponent(
  WorkflowWebSearchDisplayComponent,
  downloadWorkflowWebSearch,
  workflowWebSearchToMarkdown
)
